// ** React Imports
import React, { useEffect, useLayoutEffect, useState } from 'react'

// ** 3rd Party Libraries
import { useDebouncedCallback } from 'use-debounce'

// ** MUI Imports
import { Grid, Typography, Card, CardContent, Button, TextField, Stack, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import PageHeader from 'core/components/page-header'
import CircularProgress from '@mui/material/CircularProgress'
import Snackbar from '@mui/material/Snackbar'
import Grow from '@mui/material/Grow'
import InputAdornment from '@mui/material/InputAdornment'

// ** Icons Imports
import DeleteIcon from 'mdi-material-ui/Delete'
import PlusIcon from 'mdi-material-ui/Plus'

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import CustomInputs from '../components/CustomInputs'
import {
  commitmentDurationSelector,
  createCommitmentDuation,
  deleteCommitmentDuation,
  editCommitmentDuation,
  fetchCommitmentDuationData,
  cleanMessages
} from 'store/metaData/commitmentDuration'

// Styled Card component
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  border: `solid 1px ${theme.palette.grey[300]}`,
  paddingLeft: 3,
  paddingRight: 3
}))

// Styled Button Component
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'uppercase',
  marginLeft: 2,
  marginRight: '1rem',
  fontWeight: 600,
  boxShadow: theme.shadows[4],
  color: theme.palette.common.blue,
  backgroundColor: theme.palette.common.white,
  '&:disabled': {
    color: theme.palette.grey[700],
    boxShadow: 'none'
  },
  '&:hover': {
    backgroundColor: theme.palette.common.white
  }
}))

const CommitmentDuration = () => {
  // ** Redux dispatcher
  const dispatch = useDispatch()

  // ** States
  const [edit, setEdit] = useState(null)
  const [draft, setDraft] = useState(null)
  const [open, setOpen] = useState(false)
  const [data, setData] = useState([])

  // ** Selectors
  const { commitmentDurations, success, error, loading } = useSelector(commitmentDurationSelector)

  // ** Fetch data
  useEffect(() => {
    dispatch(fetchCommitmentDuationData())
  }, [])

  useEffect(() => {
    if (commitmentDurations?.length > 0) setData(commitmentDurations)
  }, [commitmentDurations])

  useLayoutEffect(() => {
    if (success == 'CREATE' || success == 'UPDATE') {
      setDraft(null)
      setEdit(null)
      setOpen(true)
    }
    setTimeout(() => {
      dispatch(cleanMessages())
    }, 1500)
  }, [success, error])

  // ** Functions
  const addDraftHandler = () => {
    setDraft({ name: '', value: '' })
    setEdit(null)
  }
  const deleteDraftHandler = () => {
    setDraft(null)
  }

  const addCommitmentDurationHandler = () => {
    if (edit) {
      dispatch(editCommitmentDuation({ id: edit, ...draft }))
    } else {
      dispatch(createCommitmentDuation({ ...draft, name: 'commitmentDuration-' + draft.value }))
    }
  }

  const deleteCommitmentDurationHandler = id => {
    dispatch(deleteCommitmentDuation(id))
  }

  const handleCloseMessage = () => {
    setOpen(false)
  }

  const debounced = useDebouncedCallback(
    event => {
      const { name, value } = event.target
      setDraft(prevState => {
        return { ...prevState, [name]: value }
      })
    },
    500,
    // The maximum time func is allowed to be delayed before it's invoked:
    { maxWait: 3000 }
  )

  const discardChanges = () => {
    setEdit(null)
    setDraft(null)
  }

  const onChangeHandler = (event, id) => {
    const { name, value } = event.target
    setData(state => {
      const initialObject = commitmentDurations.find(el => el.id === id)
      const object = state.find(el => el.id === id)
      if (initialObject.value !== value && !!value) {
        setDraft({ name: object.name, value: value || 0 })
        setEdit(id)
      } else {
        setDraft(null)
        setEdit(null)
      }
      return state.map(item => (item.id === id ? { ...item, value: value ?? 0 } : item))
    })
  }

  return (
    <Grid container spacing={6}>
      <PageHeader
        title={
          <Typography variant='h5' sx={{ color: 'common.blue', fontWeight: 600 }}>
            Commitment Duration List
          </Typography>
        }
      />
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            {loading == 'GET' && (
              <Grid xs={12} sx={{ ml: '1rem', mb: 5 }}>
                <CircularProgress size='3rem' />
              </Grid>
            )}
            {loading != 'GET' &&
              data?.map((commitment, index) => (
                <Grid key={commitment.id} item xs={12} sx={{ display: 'flex', mb: 5 }}>
                  <Stack direction='row' alignItems='center' gap={1} sx={{ my: 2 }}>
                    <TextField
                      sx={{ mr: 5 }}
                      label='Commitment Duration'
                      size='small'
                      InputProps={{
                        endAdornment: <InputAdornment position='end'>year</InputAdornment>
                      }}
                      value={commitment.value}
                      type='number'
                      name='value'
                      onChange={event => onChangeHandler(event, commitment.id)}
                      error={error}
                      helperText={error && 'Value must be positive'}
                    />
                    <IconButton onClick={() => deleteCommitmentDurationHandler(commitment.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Grid>
                // <CustomInputs
                //   key={index}
                //   type='commitmentDuration'
                //   setEdit={setEdit}
                //   data={commitment}
                //   setDraft={setDraft}
                //   deleteHandler={deleteCommitmentDurationHandler}
                // />
              ))}
            {draft && !edit ? (
              <Grid item xs={12} sx={{ display: 'flex', mb: 5 }}>
                <Stack direction='row' alignItems='center' gap={3} sx={{ my: 2 }}>
                  <TextField
                    sx={{ mr: 5 }}
                    type='number'
                    label='Commitment duration'
                    size='small'
                    name='value'
                    InputProps={{
                      endAdornment: <InputAdornment position='end'>month</InputAdornment>
                    }}
                    onChange={debounced}
                  />
                  <IconButton onClick={deleteDraftHandler}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Grid>
            ) : null}
            <Grid xs={12} sx={{ mb: 5 }}>
              <StyledButton variant='text' disabled={draft || edit} onClick={addDraftHandler}>
                <PlusIcon sx={{ mr: 2 }} />
                Add Commitment Duration
              </StyledButton>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex' }}>
              <StyledButton
                variant='contained'
                disabled={!draft?.value}
                onClick={addCommitmentDurationHandler}
                startIcon={(loading == 'CREATE' || loading == 'UPDATE') && <CircularProgress size='1rem' />}
              >
                save
              </StyledButton>
              <StyledButton variant='contained' disabled={!(edit || draft)} onClick={discardChanges}>
                discard changes
              </StyledButton>
            </Grid>
          </CardContent>
        </StyledCard>
      </Grid>
      <Snackbar
        sx={{ mt: '3rem' }}
        open={open}
        onClose={handleCloseMessage}
        message='Action done successfully !'
        autoHideDuration={2000}
        key={'top' + 'right'}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Grow}
      />
    </Grid>
  )
}

export default CommitmentDuration
